import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, UserPlus, Key } from 'lucide-react';
import AppHeader from '../components/AppHeader';

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'employee'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post('/auth/register', newUser);
      toast.success('User created successfully');
      setDialogOpen(false);
      setNewUser({ name: '', email: '', password: '', role: 'employee' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await axios.put('/auth/admin-reset-password', {
        user_id: selectedUser.id,
        new_password: newPassword
      });
      toast.success('Password reset successfully');
      setResetDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const openResetDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setResetDialogOpen(true);
  };

  return (
    <div className="dashboard" data-testid="user-management">
      <AppHeader />

      <main className="dashboard-content">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0 }} data-testid="page-title">
            User Management
          </h1>
        </div>

        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600' }}>Team Members</h2>
            <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="outline" data-testid="invite-user-button" onClick={async () => {
              const email = window.prompt('Teammate email to invite (they will choose their own password):');
              if (!email) return;
              const role = window.confirm('Invite as ADMIN? (Cancel = employee)') ? 'admin' : 'employee';
              try { await axios.post('/auth/invite', { email, role }); toast.success(`Invitation sent to ${email}`); } catch (e) { toast.error(e?.response?.data?.detail || 'Invite failed'); }
            }}>Invite by email</Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-user-button">
                  <UserPlus size={18} /> Add User
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="add-user-dialog">
                <DialogHeader>
                  <DialogTitle>Add New User</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateUser}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div className="form-group">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        value={newUser.name}
                        onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                        required
                        data-testid="new-user-name-input"
                      />
                    </div>
                    <div className="form-group">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        required
                        data-testid="new-user-email-input"
                      />
                    </div>
                    <div className="form-group">
                      <Label htmlFor="password">Password</Label>
                      <Input
                        id="password"
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        required
                        data-testid="new-user-password-input"
                      />
                    </div>
                    <div className="form-group">
                      <Label htmlFor="role">Role</Label>
                      <Select
                        value={newUser.role}
                        onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                      >
                        <SelectTrigger id="role" data-testid="new-user-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="employee">Employee</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="w-full" data-testid="submit-user-button">
                      Create User
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            </div>

            {/* Password Reset Dialog */}
            <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
              <DialogContent data-testid="reset-password-dialog">
                <DialogHeader>
                  <DialogTitle>Reset Password for {selectedUser?.name}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleResetPassword}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <p style={{ color: '#718096', fontSize: '14px' }}>
                      Enter a new password for this user. They will need to use this password to log in.
                    </p>
                    <div className="form-group">
                      <Label htmlFor="newPassword">New Password</Label>
                      <Input
                        id="newPassword"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={6}
                        data-testid="reset-password-input"
                      />
                      <p style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                        Password must be at least 6 characters
                      </p>
                    </div>
                    <Button type="submit" className="w-full" data-testid="confirm-reset-button">
                      Reset Password
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <p>Loading users...</p>
          ) : (
            <table className="proposals-table" data-testid="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} data-testid={`user-row-${user.id}`}>
                    <td data-testid={`user-name-${user.id}`}>{user.name}</td>
                    <td data-testid={`user-email-${user.id}`}>{user.email}</td>
                    <td>
                      <span
                        className={`status-badge ${user.role === 'admin' ? 'status-paid' : 'status-sent'}`}
                        data-testid={`user-role-${user.id}`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td data-testid={`user-created-${user.id}`}>
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openResetDialog(user)}
                        data-testid={`reset-password-${user.id}`}
                      >
                        <Key size={16} style={{ marginRight: '4px' }} />
                        Reset Password
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
};

export default UserManagement;